import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ContributionPanel from "@/components/ContributionPanel";
import SupplierCategoriesManager from "@/components/SupplierCategoriesManager";
import { industryIcon } from "@/lib/industryIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useBulkSelect, BulkBar } from "@/components/BulkBar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Loader2, ChevronLeft, ChevronRight, PieChart, TrendingUp, Download } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const PALETTE = ["#1B4383", "#2F6B45", "#A87F31", "#6D4AA6", "#C24E4E", "#2C8BB0", "#B0632C", "#4B6584", "#8E44AD", "#16A085"];
const sumAll = (obj) => Object.values(obj || {}).reduce((s, v) => s + (v || 0), 0);
const monthShort = (m) => {
  const [y, mm] = (m || "").split("-");
  const names = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(mm, 10)] || m} '${(y || "").slice(2)}`;
};

async function downloadExport(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    setTimeout(() => window.URL.revokeObjectURL(url), 30000);
  } catch { toast.error("Export failed"); }
}

function TrendChart({ trend, colorMap, currency, onCurrency }) {
  const WINDOW = 6;
  const [end, setEnd] = useState(null);
  const allRows = trend?.data || [];
  useEffect(() => { setEnd(allRows.length); }, [allRows.length]);
  if (!trend) return null;
  const inds = trend.industries;
  const endIdx = end == null ? allRows.length : Math.min(end, allRows.length);
  const startIdx = Math.max(0, endIdx - WINDOW);
  const data = allRows.slice(startIdx, endIdx).map((d) => ({ ...d, month: monthShort(d.month) }));
  const hasData = allRows.some((d) => inds.some((i) => (d[i.label] || 0) > 0));
  const canPrev = startIdx > 0;
  const canNext = endIdx < allRows.length;
  return (
    <div className="mb-6 rounded-xl border border-border bg-white p-5" data-testid="industry-trend">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#E6ECF5] text-[#1B4383]"><TrendingUp className="h-4 w-4" /></span>
          <div>
            <div className="font-heading font-semibold text-slate-900">Quoted Value Trend</div>
            <div className="text-xs text-muted-foreground">Monthly quoted value by industry ({trend.currency})</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            <button onClick={() => setEnd((e) => Math.max(WINDOW, (e == null ? allRows.length : e) - 1))} disabled={!canPrev} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30" title="Older months" data-testid="industry-trend-prev"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setEnd((e) => Math.min(allRows.length, (e == null ? allRows.length : e) + 1))} disabled={!canNext} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30" title="Newer months" data-testid="industry-trend-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="flex rounded-md border border-border p-0.5">
            {["USD", "TZS"].map((c) => (
              <button key={c} onClick={() => onCurrency(c)} className={`rounded px-2.5 py-1 text-xs font-medium ${currency === c ? "bg-brand text-white" : "text-muted-foreground hover:text-slate-900"}`} data-testid={`industry-trend-cur-${c}`}>{c}</button>
            ))}
          </div>
        </div>
      </div>
      {!hasData ? (
        <div className="rounded-lg bg-slate-50 py-10 text-center text-sm text-muted-foreground">
          Not enough history yet — quotes will chart here month by month.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => (v >= 1e6 ? `${+(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${Math.round(v / 1e3)}k` : v)} />
            <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {inds.map((i, idx) => (
              <Line key={i.value} type="monotone" dataKey={i.label} stroke={(colorMap && colorMap[i.value]) || PALETTE[idx % PALETTE.length]}
                strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 5 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default function Industries() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [trend, setTrend] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [trendCcy, setTrendCcy] = useState("USD");
  const [addOpen, setAddOpen] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isOwner = (user?.email || "").toLowerCase() === "ronick@somcoregroup.com";
  const sel = useBulkSelect();
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/industries");
    setRows(data);
    api.get("/industries/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/industries/trends?currency=${trendCcy}&full=true`).then((r) => setTrend(r.data)).catch(() => {});
  }, [trendCcy]);

  const currencies = React.useMemo(() => {
    const extra = (analytics?.currencies || []).filter((c) => c !== "USD" && c !== "TZS");
    return ["USD", "TZS", ...extra];
  }, [analytics]);

  const add = async () => {
    if (!newLabel.trim()) { toast.error("Enter an industry name."); return; }
    setAdding(true);
    try {
      await api.post("/industries", { label: newLabel.trim() });
      setNewLabel(""); setAddOpen(false); await load(); toast.success("Industry added");
    } catch (e) { toast.error(apiError(e)); } finally { setAdding(false); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/industries/${toDelete.value}`);
      setToDelete(null); await load(); toast.success("Industry deleted");
    } catch (e) { toast.error(apiError(e)); setToDelete(null); }
  };

  const bulkDelete = async () => {
    const values = [...sel.selected];
    if (!window.confirm(`Delete ${values.length} industr${values.length !== 1 ? "ies" : "y"}? Any still used by clients will be skipped.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/industries/bulk-delete", { values });
      if (data.deleted.length) toast.success(`Deleted ${data.deleted.length} industr${data.deleted.length !== 1 ? "ies" : "y"}`);
      if (data.skipped && data.skipped.length) {
        toast.error(`Couldn't delete ${data.skipped.map((s) => `${s.label} (${s.count} client${s.count !== 1 ? "s" : ""})`).join(", ")} — still in use.`);
      }
      sel.clear(); await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBulkBusy(false); }
  };

  const colorMap = {};
  (analytics?.industries || []).forEach((i, idx) => { colorMap[i.value] = PALETTE[idx % PALETTE.length]; });

  const items = (analytics?.industries || []).map((i) => {
    const q = sumAll(i.quoted_value), w = sumAll(i.won_value);
    return {
      key: i.value, label: i.label, meta: `${i.clients} client${i.clients !== 1 ? "s" : ""} · ${i.rfqs} RFQ${i.rfqs !== 1 ? "s" : ""}`,
      conv: q > 0 ? `${Math.round((w / q) * 100)}% won` : null,
      metricValues: { quoted_value: i.quoted_value, won_value: i.won_value },
    };
  });

  return (
    <AppLayout title="Industries & Categories" actions={
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => downloadExport("/industries/export", `somcore_industries_${new Date().toISOString().slice(0, 10)}.xlsx`)} data-testid="export-industries-button">
          <Download className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Export</span>
        </Button>
        <Button onClick={() => { setNewLabel(""); setAddOpen(true); }} data-testid="add-industry-button">
          <Plus className="h-4 w-4 sm:mr-2" /><span className="sm:hidden">New</span><span className="hidden sm:inline">New Industry</span>
        </Button>
      </div>
    }>
      <div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {analytics && (
            <ContributionPanel
              title="Business by Industry"
              subtitle="Share of value driving Somcore's pipeline"
              Icon={PieChart}
              currencies={currencies}
              currency={currency}
              onCurrencyChange={setCurrency}
              metrics={[{ key: "quoted_value", label: "Quoted" }, { key: "won_value", label: "Won (Closed)" }]}
              items={items}
              colorMap={colorMap}
              onOpen={(v) => navigate(`/industries/${v}`)}
              testid="bbi"
            />
          )}

          <TrendChart trend={trend} colorMap={colorMap} currency={trendCcy} onCurrency={setTrendCcy} />
        </div>

        <div className="mt-8">
          <h3 className="mb-3 font-heading text-lg font-semibold text-slate-900">Manage Industries</h3>
          {/* List */}
          <div className="overflow-x-auto rounded-lg border border-border bg-white">
          {rows === null ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">No industries yet — add one above.</div>
          ) : (
            <div className="divide-y divide-border">
              {rows.map((r) => {
                const Icon = industryIcon(r.value || r.label);
                return (
                  <div
                    key={r.value}
                    role="button"
                    tabIndex={0}
                    onClick={() => (isAdmin && sel.selected.size > 0) ? sel.toggle(r.value) : navigate(`/industries/${r.value}`)}
                    onKeyDown={(e) => e.key === "Enter" && navigate(`/industries/${r.value}`)}
                    className={`group flex cursor-pointer items-center gap-3 px-5 py-3.5 transition-colors hover:bg-slate-50 ${sel.isSelected(r.value) ? "bg-[#EEF3FA]" : ""}`}
                    data-testid={`industry-open-${r.value}`}
                  >
                    {isAdmin && (
                      <span onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={sel.isSelected(r.value)} onCheckedChange={() => sel.toggle(r.value)} aria-label="Select industry" data-testid={`bulk-select-industry-${r.value}`} />
                      </span>
                    )}
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#E6ECF5] text-[#1B4383]">
                      <Icon className="h-4.5 w-4.5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900">{r.label}</div>
                      <div className="text-xs text-muted-foreground">{r.count} client{r.count !== 1 ? "s" : ""} using it</div>
                    </div>
                    <Button
                      size="sm" variant="ghost"
                      onClick={(e) => { e.stopPropagation(); if (r.count === 0) setToDelete(r); }}
                      disabled={r.count > 0}
                      title={r.count > 0 ? "In use by clients — reassign them first" : "Delete industry"}
                      data-testid={`industry-delete-${r.value}`}
                    >
                      <Trash2 className={`h-4 w-4 ${r.count > 0 ? "text-slate-300" : "text-rose-500"}`} />
                    </Button>
                    <ChevronRight className="h-5 w-5 text-slate-300 transition-colors group-hover:text-slate-500" />
                  </div>
                );
              })}
            </div>
          )}
          </div>
        </div>
      </div>

      <BulkBar count={sel.selected.size} onClear={sel.clear} onDelete={bulkDelete} canDelete={isAdmin} deleting={bulkBusy} />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="industry-add-dialog">
          <DialogHeader>
            <DialogTitle>New Industry</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="industry-name">Industry name</Label>
            <Input id="industry-name" placeholder="e.g. Renewable Energy" value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
              autoFocus data-testid="industry-new-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={add} disabled={adding} data-testid="industry-add-btn">
              {adding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}Add Industry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SupplierCategoriesManager />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="industry-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{toDelete?.label}”?</AlertDialogTitle>
            <AlertDialogDescription>This removes it from the industry dropdown. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="industry-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
